import { supabase } from '../supabaseClient'
import { EficaciaProteccion, FrecuenciaDominanteImpulso } from './ruido'

// ===== Cálculos de ruido por local/puesto de trabajo (multi-empresa) =====
// Mismo patrón que gestion-tecnica-iluminacion.ts / lighting_calculations:
// inputs y resultado se guardan como jsonb libre, tipados aquí para que el
// resto del código (formulario, PDF) no maneje `any`.

export interface NoiseCalculationInputsContinuo {
  tipoRuido: 'continuo'
  lecturas: number[][]
  horasExposicion: number
  H: number
  M: number
  L: number
  SNR: number
  mfPorBanda: number[]
  sigmaPorBanda: number[]
  eficacia: EficaciaProteccion
  minutosSinProtectorPorHora: number
}

export interface NoiseCalculationInputsImpulsivo {
  tipoRuido: 'impulsivo'
  LCpico: number
  frecuenciaDominante: FrecuenciaDominanteImpulso
  H: number
  M: number
  L: number
}

export type NoiseCalculationInputs = NoiseCalculationInputsContinuo | NoiseCalculationInputsImpulsivo

// Resultado calculado en RuidoMethodologyPage.tsx a partir de src/lib/ruido.ts
// — no existe un calculateFullReport() único (a diferencia de iluminacion.ts)
// porque el motor de Ruido expone funciones puras independientes; esta forma
// es el contrato de persistencia que las agrupa para guardar/mostrar/imprimir.
export interface NoiseCalculationResultadoContinuo {
  LA: number
  LC: number
  LAp: number       // método de bandas de octava (más preciso)
  LAp_HML: number
  LAp_SNR: number
  laeqDiario: number
  cumpleAM196: boolean
  laeqEfectivoReal: number
  cumpleAM196Real: boolean
}

export interface NoiseCalculationResultadoImpulsivo {
  LCpicoAtenuado: number
  cumpleLimitePico: boolean
}

export type NoiseCalculationResultado = NoiseCalculationResultadoContinuo | NoiseCalculationResultadoImpulsivo

export interface NoiseCalculationRow {
  id: string
  company_id: string
  job_position_id: string | null
  descripcion: string
  tipo_ruido: 'continuo' | 'impulsivo'
  inputs: NoiseCalculationInputs
  resultado: NoiseCalculationResultado
  cumple_normativa: boolean
  created_by: string | null
  created_at: string
  job_position_name: string | null
}

export interface CreateNoiseCalculationInput {
  companyId: string
  jobPositionId?: string
  descripcion: string
  tipoRuido: 'continuo' | 'impulsivo'
  inputs: NoiseCalculationInputs
  resultado: NoiseCalculationResultado
  cumpleNormativa: boolean
  createdBy: string | null
}

export async function createNoiseCalculation(input: CreateNoiseCalculationInput): Promise<void> {
  const { error } = await supabase.from('noise_calculations').insert({
    company_id: input.companyId,
    job_position_id: input.jobPositionId ?? null,
    descripcion: input.descripcion,
    tipo_ruido: input.tipoRuido,
    inputs: input.inputs,
    resultado: input.resultado,
    cumple_normativa: input.cumpleNormativa,
    created_by: input.createdBy,
  })
  if (error) throw error
}

export async function fetchNoiseCalculations(companyId: string): Promise<NoiseCalculationRow[]> {
  if (!companyId) return []
  const { data, error } = await supabase
    .from('noise_calculations')
    .select('id, company_id, job_position_id, descripcion, tipo_ruido, inputs, resultado, cumple_normativa, created_by, created_at, job_positions(name)')
    .eq('company_id', companyId)
    .order('created_at', { ascending: false })
  if (error) throw error
  return (data ?? []).map((r: any) => ({
    id: r.id,
    company_id: r.company_id,
    job_position_id: r.job_position_id,
    descripcion: r.descripcion,
    tipo_ruido: r.tipo_ruido,
    inputs: r.inputs,
    resultado: r.resultado,
    cumple_normativa: r.cumple_normativa,
    created_by: r.created_by,
    created_at: r.created_at,
    job_position_name: r.job_positions?.name ?? null,
  })) as NoiseCalculationRow[]
}

// ===== Catálogo de protectores auditivos (mismo patrón que fetchLuminaireCatalog() /
// luminaire_catalog en gestion-tecnica-iluminacion.ts: catálogo global) =====

export interface HearingProtectorCatalogItem {
  id: string
  manufacturer: string
  model: string
  protector_type: 'insercion' | 'orejera' | 'insercion_con_banda'
  h_value: number
  m_value: number
  l_value: number
  snr_value: number
  notes: string | null
  created_at: string
}

export async function fetchHearingProtectorCatalog(): Promise<HearingProtectorCatalogItem[]> {
  const { data, error } = await supabase
    .from('hearing_protector_catalog')
    .select('id, manufacturer, model, protector_type, h_value, m_value, l_value, snr_value, notes, created_at')
    .order('manufacturer')
    .order('model')
  if (error) throw error
  return (data ?? []) as HearingProtectorCatalogItem[]
}
